import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import DonateButton from "../components/site/DonateButton";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";

export default function Zimbabwe() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title="Zimbabwe"
        path="/zimbabwe"
        description="Mount Moriah Relief has a particular, but not exclusive, focus on supporting vulnerable communities in Zimbabwe through humanitarian assistance and responsible local partnerships."
      />
      <PageHero
        eyebrow="Where We Work"
        title="Zimbabwe: a focus close to our heart"
        image={IMAGES.victoriaFalls.src}
        imageAlt={IMAGES.victoriaFalls.alt}
      >
        <p>
          A land of remarkable beauty and resilient people. Our charitable mission has a
          particular, though not exclusive, focus on supporting vulnerable communities here.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 reveal">
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
              Why Zimbabwe
            </h2>
            <div className="mt-6 space-y-5 text-charcoal/80 leading-relaxed">
              <p>
                Many communities in Zimbabwe face sustained economic hardship — where poverty
                affects access to food, education and essential services. Behind every statistic
                are families of real dignity, capability and hope.
              </p>
              <p>
                Mount Moriah Relief's response is relationship-first: we are building responsible
                partnerships with trusted local organisations so that any assistance we give is
                appropriate, accountable and shaped by the communities it serves.
              </p>
              <p>
                Our intended areas of support include humanitarian assistance, education and
                learning, and community agriculture. As partnerships and projects are formally
                confirmed, we will report them here and in our news section — accurately, and
                never ahead of reality.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <DonateButton testId="zimbabwe-donate-button">Support This Work</DonateButton>
              <Link to="/our-work" className="inline-flex items-center border border-forest text-forest px-8 py-3.5 font-medium hover:bg-forest hover:text-white transition-colors duration-300" data-testid="zimbabwe-work-link">
                Explore Our Work
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-8 reveal">
            <figure>
              <img src={IMAGES.agriculture.src} alt={IMAGES.agriculture.alt} loading="lazy"
                className="w-full aspect-[4/3] object-cover border border-sandstone" />
              <figcaption className="mt-2 text-xs text-charcoal/50 italic">
                Community agriculture — illustrative photograph
              </figcaption>
            </figure>
            <figure>
              <img src={IMAGES.students.src} alt={IMAGES.students.alt} loading="lazy"
                className="w-full aspect-[4/3] object-cover border border-sandstone" />
              <figcaption className="mt-2 text-xs text-charcoal/50 italic">
                Education and learning — illustrative photograph
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-t border-sandstone">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
            Dignity in every image and every story
          </h2>
          <p className="mt-5 text-charcoal/80 leading-relaxed">
            The photographs on this page are illustrative, licensed images chosen to honour the
            beauty and resilience of the region. They do not depict Mount Moriah Relief
            beneficiaries or projects. When we publish stories from our work, they will be shared
            with informed consent and handled with dignity.
          </p>
        </div>
      </section>
    </div>
  );
}
